import React, { useState, useEffect } from 'react';
import { paymentService } from '../../services/paymentService';
import './PSEPayment.css';

const PSEPayment = ({ amount, displayAmount, products = [], shippingData = null, couponCode = '', guestInfo = null, onSuccess, onError, onCancel }) => {
  const shownAmount = displayAmount ?? amount;
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bankLoading, setBankLoading] = useState(true);
  const [formData, setFormData] = useState({
    bank: '',
    personType: '0',
    docType: 'CC',
    docNumber: '',
    name: '',
    lastName: '',
    email: '',
    phone: '',
    address: ''
  });
  const [errors, setErrors] = useState({});

  const documentTypes = paymentService.getDocumentTypes();
  const personTypes = paymentService.getPersonTypes();

  useEffect(() => {
    loadBanks();
  }, []);

  // Pre-fill guest info when provided
  useEffect(() => {
    if (guestInfo) {
      const parts = (guestInfo.nombre || '').trim().split(' ');
      setFormData(prev => ({
        ...prev,
        name: parts[0] || '',
        lastName: parts.slice(1).join(' ') || '',
        email: guestInfo.email || '',
        phone: shippingData?.telefono_contacto || prev.phone,
      }));
    }
  }, [guestInfo, shippingData]);

  const loadBanks = async () => {
    try {
      setBankLoading(true);
      const response = await paymentService.getPSEBanks();
      
      if (response.success && response.banks) {
        // Filter only enabled banks
        const enabledBanks = response.banks.filter(bank => bank.enabled);
        setBanks(enabledBanks);
      } else if (response.banks) {
        setBanks(response.banks);
      } else {
        setBanks([]);
      }
    } catch (error) {
      console.error('Error loading PSE banks:', error);
      onError?.('Error cargando bancos PSE. Por favor intenta de nuevo.');
    } finally {
      setBankLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bank) {
      newErrors.bank = 'Selecciona un banco';
    }

    if (!formData.docNumber) {
      newErrors.docNumber = 'Número de documento es requerido';
    } else if (formData.docNumber.length < 7) {
      newErrors.docNumber = 'Número de documento debe tener al menos 7 dígitos';
    }

    if (!formData.name) {
      newErrors.name = 'Nombre es requerido';
    }

    if (!formData.email) {
      newErrors.email = 'Email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email no es válido';
    }

    if (!formData.phone) {
      newErrors.phone = 'Teléfono es requerido';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Teléfono debe tener 10 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Obtener la IP pública antes de enviar el pago
      let ip = '';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ip = ipData.ip;
      } catch (ipErr) {
        console.error('No se pudo obtener la IP pública', ipErr);
        ip = '';
      }
      const paymentData = {
        amount: parseFloat(amount),
        description: 'Compra en Tablero Infinito',
        name: formData.name,
        last_name: formData.lastName || '',
        email: formData.email,
        phone: formData.phone,
        address: formData.address || 'N/A',
        bank: formData.bank,
        person_type: formData.personType,
        doc_type: formData.docType,
        doc_number: formData.docNumber,
        ip: ip,
        items: products
          .filter(p => p.productId)
          .map(p => ({
            producto_id: parseInt(p.productId),
            nombre: p.name,
            cantidad: p.quantity,
            precio: p.price,
          })),
        ...(shippingData || {}),
        ...(couponCode ? { coupon_code: couponCode } : {}),
      };

      const response = await paymentService.createPSEPayment(paymentData);
      
      if (response && response.url) {
        setLoading(false);
          setTimeout(() => {
            window.location.replace(response.url);
          }, 50);
        return;
      }
      onSuccess?.(response);
    } catch (error) {
      console.error('Error creating PSE payment:', error);
      // Ignorar abortos/cancelaciones provocadas por redirección
      if (error.message === 'canceled' || error.code === 'ERR_NETWORK') return;
      const rawMsg = error?.error || error?.message || 'Error procesando el pago PSE';
      // Traducir errores comunes de ePayco a mensajes amigables
      let userMsg = rawMsg;
      if (rawMsg.includes('no se encuentra disponible')) {
        userMsg = 'El servicio PSE no está disponible en este momento. Por favor intenta de nuevo en unos minutos o usa otro método de pago.';
      } else if (rawMsg.includes('FAIL_INVALIDPARAMETERS')) {
        userMsg = 'Hubo un problema con los datos enviados al banco. Verifica tu información e intenta de nuevo.';
      } else if (rawMsg.includes('Failed to authenticate with ePayco')) {
        userMsg = 'Error de conexión con el procesador de pagos. Intenta de nuevo en unos minutos.';
      }
      onError?.(userMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (bankLoading) {
    return (
      <div className="pse-payment-container">
        <div className="loading-banks">
          <div className="spinner"></div>
          <p>Cargando bancos disponibles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pse-payment-container">
      <div className="pse-header">
        <div className="pse-logo">
          <span className="bank-icon">🏦</span>
          <h3>Pago PSE</h3>
        </div>
        <div className="amount-display">
          <span className="amount-label">Total a pagar:</span>
          <span className="amount-value">{formatCurrency(shownAmount)}</span>
        </div>
      </div>

      <div className="pse-info">
        <div className="info-item">
          <span className="info-icon">🔒</span>
          <span>Pago seguro a través de tu banco</span>
        </div>
        <div className="info-item">
          <span className="info-icon">⚡</span>
          <span>Procesamiento en tiempo real</span>
        </div>
        <div className="info-item">
          <span className="info-icon">✅</span>
          <span>Sin costos adicionales</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="pse-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="bank">Banco *</label>
            <select
              id="bank"
              name="bank"
              value={formData.bank}
              onChange={handleInputChange}
              className={errors.bank ? 'error' : ''}
              disabled={loading}
            >
              <option value="">Selecciona tu banco</option>
              {banks.map((bank) => (
                <option key={bank.id || bank.code} value={bank.code || bank.id}>
                  {bank.name} {bank.type === 'digital' ? '📱' : '🏦'}
                </option>
              ))}
            </select>
            {errors.bank && <span className="error-text">{errors.bank}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="personType">Tipo de Persona</label>
            <select
              id="personType"
              name="personType"
              value={formData.personType}
              onChange={handleInputChange}
              disabled={loading}
            >
              {personTypes.map((type) => (
                <option key={type.code} value={type.code}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="docType">Tipo de Documento</label>
            <select
              id="docType"
              name="docType"
              value={formData.docType}
              onChange={handleInputChange}
              disabled={loading}
            >
              {documentTypes.map((type) => (
                <option key={type.code} value={type.code}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="docNumber">Número de Documento *</label>
            <input
              type="text"
              id="docNumber"
              name="docNumber"
              value={formData.docNumber}
              onChange={handleInputChange}
              className={errors.docNumber ? 'error' : ''}
              placeholder="Ej: 12345678"
              disabled={loading}
            />
            {errors.docNumber && <span className="error-text">{errors.docNumber}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Nombres *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Nombres"
              disabled={loading}
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Apellidos</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Apellidos"
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="ejemplo@email.com"
              disabled={loading}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phone">Teléfono *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={errors.phone ? 'error' : ''}
              placeholder="3001234567"
              disabled={loading}
            />
            {errors.phone && <span className="error-text">{errors.phone}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address">Dirección</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Dirección (opcional)"
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.bank}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Procesando...
              </>
            ) : (
              <>
                <span className="payment-icon">🔒</span>
                Pagar {formatCurrency(shownAmount)}
              </>
            )}
          </button>
        </div>
      </form>

      <div className="pse-footer">
        <p className="security-note">
          🔒 Tu información está protegida por encriptación SSL de 256 bits
        </p>
        <p className="process-note">
          ℹ️ Serás redirigido al sitio web de tu banco para completar la transacción
        </p>
      </div>
    </div>
  );
};

export default PSEPayment;
